/**
 * @file    validation.ts
 * @scope   SHARED
 * @summary Decides whether the drawer's Save button must stay disabled.
 * @consumes chartsWithTextControllers capability table
 * @produces isSaveDisabled
 * @sideEffects None. Enterprise validation is injected as a callback (DIP), so this module never
 *              imports anything from `components/DiyEnterprise/*`.
 */
import { chartsWithTextControllers } from '../constants/chartCapabilities';

/** Everything the Save guard needs to know about the current drawer session. */
export type SaveGuardContext = {
  /** Medusa query builder reports a complete, valid query. */
  queryIsValid: boolean;
  /** Text block content passes its own validation. */
  textContentIsValid: boolean;
  /** ENTERPRISE-ONLY: widget is an Enterprise V2 configuration. */
  isDiyWidgetEngine: boolean;
  /**
   * ENTERPRISE-ONLY: injected enterprise validator.
   * Kept as a callback so this shared module stays decoupled from the enterprise domain.
   */
  isEnterpriseInvalid: () => boolean;
  /** The display name input is flagged as required-but-empty. */
  requiredDisplayName: boolean;
  /** At least one business rule applies to the selected chart. */
  hasApplicableRules: boolean;
  /** The current question/calculation combination breaks every applicable rule. */
  requiredQuestionsCalculations: boolean;
};

/**
 * Evaluates the Save guard in priority order. The first matching branch wins, mirroring the
 * original inline implementation exactly.
 *
 * 1. Y-axis bounds are inconsistent (min greater than max).
 * 2. Medusa-backed widget with an incomplete query.
 * 3. Text widget with invalid content.
 * 4. ENTERPRISE-ONLY: enterprise configuration is incomplete.
 * 5. LEGACY-ONLY: rule engine rejects the question/calculation combination.
 *
 * @param customWidget Widget currently being edited.
 * @param context      Validation flags collected by the drawer.
 * @returns `true` when Save must be disabled.
 */
export const isSaveDisabled = (customWidget: any, context: SaveGuardContext): boolean => {
  const yAxisValues = customWidget?.options?.optYAxisValues;
  if (yAxisValues) {
    return yAxisValues.min > yAxisValues.max || yAxisValues.max < yAxisValues.min;
  }

  if (customWidget.hasMedusaQuery) return !context.queryIsValid;

  if (chartsWithTextControllers.includes(customWidget.alias)) return !context.textContentIsValid;

  // ENTERPRISE-ONLY: enterprise widgets ignore the legacy rule engine entirely.
  if (context.isDiyWidgetEngine) return context.isEnterpriseInvalid();

  // LEGACY-ONLY: rule engine result.
  if (context.requiredDisplayName || context.hasApplicableRules) {
    return context.requiredQuestionsCalculations;
  }

  return false;
};
